/**
 * The layer manager, React side: how deep a layer is, and the z-index that
 * gives it.
 *
 * Every floating layer (popover, menu, select, tooltip) provides its depth to
 * what it renders, so a layer opened inside it stacks above it; sheets and
 * dialogs opened from the page sit in the page band and do not add depth. The
 * numbers themselves come from `src/theme/layerStack.ts`, which builds them
 * out of the `--z-*` tokens: no component writes a z-index of its own.
 *
 * Esc and outside presses are Zag's layer stack (each Chakra overlay registers
 * as a dismissable layer, and `trackEscapeLayer` below adds the layers Zag
 * does not track: tooltips and the phone sheet), which is what makes Esc
 * close exactly the topmost layer across a dialog, a menu in it, a popover
 * from that menu and a tooltip in any of them.
 *
 * Spec: specs/design-system/ledger-layers.feature
 * History: https://github.com/langwatch/langwatch/issues/2519
 */
import { trackDismissableElement } from "@zag-js/dismissable";
import {
  createContext,
  type Ref,
  useCallback,
  useContext,
  useRef,
} from "react";
import { type LayerKind, layerZIndex } from "~/theme/layerStack";

/** How many floating layers enclose this point of the tree. */
export const OverlayDepthContext = createContext(0);

/**
 * A floating layer (popover, menu, select, tooltip): one deeper than whatever
 * encloses it. Returns the z-index string for its positioner and the depth to
 * provide to its content through `OverlayDepthContext`.
 */
export function useOverlayZIndex(): { zIndex: string; depth: number } {
  const parentDepth = useContext(OverlayDepthContext);
  const depth = parentDepth + 1;
  return { zIndex: layerZIndex({ kind: "popover", depth }), depth };
}

/**
 * A sheet or a dialog. Opened from the page it stays in the page band and the
 * recipe positions it (`zIndex` is undefined, `depth` 0). Opened from inside a
 * floating layer, it moves into the floating band one step above that layer,
 * and its own content continues from there.
 */
export function useModalLayer({
  kind,
}: {
  kind: Extract<LayerKind, "sheet" | "dialog">;
}): {
  zIndex: string | undefined;
  depth: number;
} {
  const parentDepth = useContext(OverlayDepthContext);
  if (parentDepth <= 0) return { zIndex: undefined, depth: 0 };
  const depth = parentDepth + 1;
  return { zIndex: layerZIndex({ kind, depth }), depth };
}

/**
 * Pins a z-index on a positioner Zag also styles. Zag writes `--z-index` and
 * `--layer-index` inline from its own stack; `!important` keeps the manager's
 * order in charge of the cross-component stacking.
 */
export function pinLayerZIndex(zIndex: string | undefined) {
  return (node: HTMLElement | null) => {
    if (node && zIndex) node.style.setProperty("z-index", zIndex, "important");
  };
}

/**
 * Whether this point of the tree is inside a modal dialog.
 *
 * A modal dialog hides everything outside itself from assistive technology
 * when it opens (Zag's `aria-hidden` walk over `<body>`). A popover or menu
 * inside it is portalled to `<body>`, and when it is already mounted (closed)
 * at that moment it is hidden too, so a screen reader never reaches it. The
 * Popover and Menu wrappers read this and mount their content only while
 * open inside a modal, which keeps them out of that walk.
 */
export const ModalLayerContext = createContext(false);

/**
 * The last menu button the person used. A dialog opened from a menu item is
 * opened after the menu closes and unmounts its items, so the element that
 * had focus when the dialog opened is gone, and focus would fall to `<body>`
 * on close. The dialog asks here instead and returns focus to the menu's
 * button, the row's "more" button in a table.
 */
let lastMenuOpener: HTMLElement | null = null;

export function rememberMenuOpener(element: HTMLElement | null) {
  lastMenuOpener = element;
}

/**
 * A menu closed. A dialog opened from one of its items renders in the same
 * turn as the close; once that turn is over the menu button is no longer
 * anyone's opener, so a dialog opened later (a shortcut, a timer) does not
 * send focus back to it.
 */
export function forgetMenuOpenerSoon() {
  const opener = lastMenuOpener;
  setTimeout(() => {
    if (lastMenuOpener === opener) lastMenuOpener = null;
  }, 0);
}

/**
 * Where a layer opening now should return focus when it closes, when the
 * focused element is not a usable answer itself (focus is on `<body>` or on
 * an item of a menu that is closing). `null` leaves Zag's own choice.
 */
export function openerForReturnFocus(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const active = document.activeElement as HTMLElement | null;
  const lost =
    !active || active === document.body || !!active.closest("[role='menu']");
  if (!lost) return null;
  const opener = lastMenuOpener?.isConnected ? lastMenuOpener : null;
  // One dialog per menu use: a later dialog opened from nowhere must not
  // send focus back to a menu button from long ago.
  lastMenuOpener = null;
  return opener;
}

// ── Esc: one stack, one rule ────────────────────────────────────────────────
//
// Every layer is on Zag's layer stack, in the order it opened: the Chakra
// overlays register themselves (dialog, drawer, popover, hover card, menu,
// select, combobox), and the layers Zag does not track (a tooltip, the phone
// navigation sheet) register here. Each layer on the stack listens for Esc
// and acts only when it is the topmost (`layerStack.isTopMost`), so exactly
// one layer closes.
//
// The layer that closes says so: its wrapper records "this press closes me"
// for that one event (`markEscapeClosesLayer`), and the guard below stops
// only a press that carries the record, so nothing further down, neither a
// layer's own key handler nor a page shortcut, acts on it. A topmost layer
// that stays open on Esc (`closeOnEscape={false}`, or an `onEscapeKeyDown`
// that prevents the close) records nothing and the key goes on to its
// contents: the run dialog's name list, Monaco. Nothing is inferred from the
// event's own flags, so a page listener's `preventDefault` never reads as a
// close. A combobox needs no record: Zag's combobox stops the key itself
// when its list closes on Esc.
//
// Spec: specs/design-system/ledger-layers.feature ("Esc closes exactly the
// topmost layer"); DEVIATIONS L-20.

/** Esc presses that close the topmost layer, recorded by that layer. */
const escapesThatClose = new WeakSet<Event>();

/**
 * Records that this Esc press closes the topmost layer, so the key stops
 * there. A layer calls it from its own Esc handler, and only when it will
 * really close.
 */
export function markEscapeClosesLayer(event: Event) {
  escapesThatClose.add(event);
}

/**
 * The `onEscapeKeyDown` a Ledger layer hands Zag (dialog, drawer, popover,
 * menu). Zag calls it only on the topmost layer. It runs the caller's own
 * handler first; when that left the press alone and the layer closes on Esc,
 * the press is recorded as the one that closes it.
 */
export function escapeKeyDownThatMarksClose<E extends Event>({
  closeOnEscape,
  onEscapeKeyDown,
}: {
  closeOnEscape?: boolean;
  onEscapeKeyDown?: (event: E) => void;
}): (event: E) => void {
  return (event) => {
    onEscapeKeyDown?.(event);
    if (closeOnEscape === false || event.defaultPrevented) return;
    markEscapeClosesLayer(event);
  };
}

/** Layers that close quietly: removing one never dismisses what sits above. */
const passiveLayers = new WeakSet<Element>();
let guardsInstalled = false;

function installLayerGuards() {
  if (guardsInstalled || typeof window === "undefined") return;
  guardsInstalled = true;
  // Zag dismisses every layer above one that is removed, since it was opened
  // from it. A tooltip is never a parent: when it closes under a list or a
  // menu that opened after it, those stay.
  window.addEventListener(
    "layer:request-dismiss",
    (event) => {
      const parent = (event as CustomEvent<{ targetLayer?: Element | null }>)
        .detail?.targetLayer;
      if (parent && passiveLayers.has(parent)) event.preventDefault();
    },
    true,
  );
  // Each layer hears Esc on the document in the capture phase. The root
  // element's capture listener runs after all of them and before anything
  // else on the way to the focused element, so an Esc that closed a layer
  // goes no further.
  document.documentElement.addEventListener(
    "keydown",
    (event) => {
      if (event.key === "Escape" && escapesThatClose.has(event)) {
        event.stopPropagation();
      }
    },
    true,
  );
}

/** Highest `--layer-index` Zag has given an open layer other than `node`. */
function coveredBy(node: HTMLElement): boolean {
  const own = Number.parseInt(node.style.getPropertyValue("--layer-index"), 10);
  if (Number.isNaN(own)) return false;
  for (const el of document.querySelectorAll<HTMLElement>(
    '[style*="--layer-index"]',
  )) {
    if (el === node || node.contains(el)) continue;
    if (el.dataset.state === "closed") continue;
    const index = Number.parseInt(
      el.style.getPropertyValue("--layer-index"),
      10,
    );
    if (index > own) return true;
  }
  return false;
}

/**
 * Records the Esc that closes a Zag layer whose machine takes no
 * `onEscapeKeyDown` (a select's list, a hover card). Zag closes those on any
 * Esc that reaches it unprevented while they are the topmost open layer, so
 * the record is made from the window, before any layer hears the key, when
 * that holds. `node` is the layer's content element, the one Zag puts on its
 * stack.
 */
export function trackEscapeCloseOf(node: HTMLElement): () => void {
  installLayerGuards();
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || event.isComposing) return;
    if (event.defaultPrevented || node.dataset.state !== "open") return;
    if (node.style.getPropertyValue("--layer-index") === "") return;
    if (coveredBy(node)) return;
    markEscapeClosesLayer(event);
  };
  window.addEventListener("keydown", onKeyDown, true);
  return () => window.removeEventListener("keydown", onKeyDown, true);
}

/**
 * `trackEscapeCloseOf` as a callback ref for the content element of a select
 * or a hover card. `forwarded` is the caller's own ref, set as well.
 */
export function useEscapeCloseRef<T extends HTMLElement>(
  forwarded?: Ref<T>,
): (node: T | null) => void {
  const stop = useRef<(() => void) | null>(null);
  return useCallback(
    (node: T | null) => {
      stop.current?.();
      stop.current = node ? trackEscapeCloseOf(node) : null;
      if (typeof forwarded === "function") forwarded(node);
      else if (forwarded) forwarded.current = node;
    },
    [forwarded],
  );
}

/**
 * Puts a layer Zag does not track on its layer stack while it is open.
 *
 * - `onEscape` runs when Esc is pressed and this is the topmost layer (the
 *   key then stops), and when a layer under this one closes.
 * - `onCovered` (a tooltip) runs when another layer opens above this one, a
 *   menu, a list or a popover: a tooltip never stays open under them.
 * - `passive` (a tooltip): closing this layer leaves the layers above it.
 *
 * Presses and focus outside are left to the caller.
 */
export function trackEscapeLayer({
  node,
  onEscape,
  onCovered,
  passive = false,
  closeOnEscape = true,
}: {
  node: HTMLElement;
  onEscape: () => void;
  onCovered?: () => void;
  passive?: boolean;
  /** False: Esc leaves this layer open and goes on to its contents. */
  closeOnEscape?: boolean;
}): () => void {
  installLayerGuards();
  if (passive) passiveLayers.add(node);
  const stop = trackDismissableElement(node, {
    type: passive ? "tooltip" : "dialog",
    pointerBlocking: false,
    onDismiss: onEscape,
    onEscapeKeyDown: (event) => {
      if (!closeOnEscape) {
        event.preventDefault();
        return;
      }
      // Zag closes this layer next, unless the press was already handled.
      if (!event.defaultPrevented) markEscapeClosesLayer(event);
    },
    onInteractOutside: (event) => event.preventDefault(),
  });
  let observer: MutationObserver | undefined;
  if (onCovered && typeof MutationObserver !== "undefined") {
    observer = new MutationObserver(() => {
      if (coveredBy(node)) onCovered();
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["style", "data-state"],
    });
  }
  return () => {
    observer?.disconnect();
    stop();
    // Zag leaves the index on a removed layer's own node; a positioner that
    // stays mounted closed must not read as a layer later.
    node.style.removeProperty("--layer-index");
    node.style.removeProperty("--nested-layer-count");
    if (passive) passiveLayers.delete(node);
  };
}

if (typeof window !== "undefined") installLayerGuards();
